import create_apollo_client from "./apollo-client";
import { gql } from "@apollo/client";
import { set_cookie } from "@core/helpers/cookie";
import { has_ssr } from "@core/helpers/render-type";
import { get_current_locate_from_cookie } from "@core/helpers/locate";
import { is_mobile_device } from "./device";

const handler_status_code = async (error) => {
	const errors = error.graphQLErrors;
	const networkError = error?.networkError;

	if (errors && errors?.[0] && errors?.[0]?.code === "401") {
		// A Server Component cannot change cookies (Next throws "Cookies can only be modified in a Server Action or Route
		// Handler"); the stale token then stays and the caller just gets status false. Route Handlers and the browser still clear it.
		try {
			await set_cookie("_token", "");
			await set_cookie("_access_id", "");
		} catch (e) {
			// nothing to do while a page renders
		}
		if (!has_ssr()) {
			if (typeof window === "undefined") return;
			window?.dispatchEvent(new CustomEvent("_logout"));
		}
	}

	// storefront dev mode: the backend no longer accepts this browser's token (expired, revoked). The browser leaves dev
	// mode by itself and reloads on the live site; a page rendering on the server leaves it to the middleware, which
	// sends the visitor to /apis/dev-mode to say so (cookies cannot be changed while a page renders)
	if (errors && errors?.[0] && errors?.[0]?.code === "dev_mode_expired" && !has_ssr() && typeof window !== "undefined") {
		window.location.assign(`/apis/dev-mode?ended=${encodeURIComponent(errors?.[0]?.errors?.dev_mode?.[0] || "expired")}&back=${encodeURIComponent(window.location.pathname + window.location.search)}`);
	}

	if (networkError?.statusCode === 400) {
		console.error("graphql 400", networkError?.result?.errors?.[0]);
	}
};

// Every core query/mutation aliases its root field `result`, and callers get that field. A null `result` (no wallet
// record, no such article, a guest) used to come back as `{ result: null }` and a falsy one (0, false, "") from a query
// as the whole object, so a caller could not tell "nothing" from "an unknown shape" (chawk-bazar, ciseco unwrapped it
// again). Now `result` is returned whatever its value. A response with other root fields beside a null `result` is
// still returned whole.
export const unwrap_result = (data) => {
	if (!data || typeof data !== "object" || !("result" in data)) return data;
	if (data.result !== null && data.result !== undefined) return data.result;
	return Object.keys(data).length === 1 ? null : data;
};

// Services hand their whole `options` on, so `gql`, `*_mockup` and other service options reached Apollo too. Only
// Apollo's own options (v3 and v4 names) are passed to `client.query` / `client.mutate`.
const APOLLO_OPTION_KEYS = [
	"context",
	"errorPolicy",
	"fetchPolicy",
	"pollInterval",
	"notifyOnNetworkStatusChange",
	"returnPartialData",
	"partialRefetch",
	"canonizeResults",
	"optimisticResponse",
	"updateQueries",
	"refetchQueries",
	"awaitRefetchQueries",
	"update",
	"onQueryUpdated",
	"keepRootFields",
];

export const apollo_options = (options) => {
	const picked = {};
	if (!options || typeof options !== "object") return picked;
	for (const key of APOLLO_OPTION_KEYS) {
		if (options[key] !== undefined) picked[key] = options[key];
	}
	return picked;
};

export const query = async (query, variables, options = {}) => {
	const client = await create_apollo_client();

	return await new Promise(async (resolve, reject) => {
		// a malformed document (or a sync Apollo invariant) throws before the promise chain exists; inside this async
		// executor that throw would never reach resolve() and the caller would wait forever
		try {
		return await client
			.query({
				query: gql`
          ${query}
			`,
				variables,
				...apollo_options(options),
			})
			.then(({ error, data }) => {
				// resolve, never reject: every service promises `{ status: false, errors }` on failure and callers only check
				// `status` (sites wrapped their calls in timeouts because a GraphQL error could reject here)
				if (error) {
					return resolve({
						errors: error?.graphQLErrors?.length ? error.graphQLErrors : [error],
						status: false,
					});
				}
				return resolve({
					data: unwrap_result(data),
					status: true,
				});
			})
			.catch(async (error) => {
				// a throw here would skip resolve() and leave the caller awaiting forever (a page hung ~2 min), so it is caught
				await handler_status_code(error).catch((e) => console.error("graphql handler_status_code failed", e));
				return resolve({
					errors: error.graphQLErrors,
					status: false,
				});
			});
		} catch (error) {
			console.error("graphql request could not be sent", error?.message || error);
			return resolve({ errors: [error], status: false });
		}
	});
};

export const mutation = async (mutation, variables, options = {}) => {
	const client = await create_apollo_client();

	return await new Promise(async (resolve, reject) => {
		// a malformed document (or a sync Apollo invariant) throws before the promise chain exists; inside this async
		// executor that throw would never reach resolve() and the caller would wait forever
		try {
		return await client
			.mutate({
				mutation: gql`
          ${mutation}
			`,
				variables,
				...apollo_options(options),
			})
			.then(({ error, data }) => {
				if (error) {
					return resolve({
						errors: error?.graphQLErrors?.length ? error.graphQLErrors : [error],
						status: false,
					});
				}
				// bfcache (all sites, asked by jument): a page left before this change is reloaded when Back brings it back
				if (!has_ssr()) {
					require("@core/helpers/client/visitor-state").mark_visitor_state_changed();
				}
				return resolve({
					data: unwrap_result(data),
					status: true,
				});
			})
			.catch(async (error) => {
				await handler_status_code(error).catch((e) => console.error("graphql handler_status_code failed", e));

				return resolve({
					errors: error.graphQLErrors,
					status: false,
				});
			});
		} catch (error) {
			console.error("graphql request could not be sent", error?.message || error);
			return resolve({ errors: [error], status: false });
		}
	});
};

export const proxy_api = async (body, header = {}) => {
	const lang = await get_current_locate_from_cookie();
	if (!has_ssr()) {
		let result = await fetch('/apis/proxy/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				lang,
				"device-type": (await is_mobile_device()) ? "mobile" : "desktop",
				...header
			},
			body: JSON.stringify(body),
			cache: "no-store",
		});

		// A 502/504 from nginx is an HTML page, not JSON: `.json()` threw `Unexpected token 'B', "Bad Gateway"` into
		// every caller (popups, banners) and Lighthouse counted the console errors (Best Practices). Like the other
		// core services, a failed request is `status: false` instead of a throw.
		try {
			result = await result.json();
		} catch {
			return { status: false, errors: [{ message: `proxy responded ${result.status}` }] };
		}

		return result;
	} else {
		throw new Error('proxy_api is not available in ssr');
	}
}
